import { ChatServiceInterface, ChatServiceConfig, ChatServiceType } from './ChatServiceInterface';
import { chatServiceFactory } from './ChatServiceFactory';
import { getActiveProvider, getProviderConfig } from '../../config/chatConfig';
import { createFileLogger } from '../../utils/logger';

const log = createFileLogger('CHAT_SERVICE');

class ChatService {
  private activeService: ChatServiceInterface | null = null;
  private isInitialized = false;

  async initialize(userConfig?: Partial<ChatServiceConfig>): Promise<void> {
    if (this.isInitialized) {
      log.warn('Chat service already initialized');
      return;
    }

    try {
      const activeProvider = getActiveProvider();
      const providerConfig = getProviderConfig(activeProvider);

      this.activeService = chatServiceFactory.createService(activeProvider);

      const config: ChatServiceConfig = {
        websiteId: (providerConfig as any).websiteId || (providerConfig as any).appId || (providerConfig as any).key,
        ...userConfig,
      };

      await this.activeService.initialize(config);

      this.isInitialized = true;
      log.info(`Chat service initialized with provider: ${activeProvider}`);
    } catch (error) {
      log.error('Failed to initialize chat service:', error);
      throw error;
    }
  }

  isReady(): boolean {
    return this.isInitialized && (this.activeService?.isReady() ?? false);
  }

  openChat(): void {
    if (!this.activeService) {
      log.warn('Chat service not initialized - cannot open chat');
      return;
    }

    try {
      this.activeService.openChat();
    } catch (error) {
      log.error('Error calling activeService.openChat():', error);
    }
  }

  closeChat(): void {
    if (!this.isReady()) return;
    this.activeService!.closeChat();
  }

  setFloatingButtonVisible(visible: boolean): void {
    if (!this.isReady()) return;
    this.activeService!.setFloatingButtonVisible(visible);
  }

  setUser(user: {
    id?: string;
    email?: string;
    name?: string;
    plan?: string;
    customData?: Record<string, any>;
  }): void {
    if (!this.isReady()) return;
    this.activeService!.setUser(user);
  }

  sendMessage(message: string): void {
    if (!this.isReady()) return;
    if (this.activeService!.sendMessage) {
      this.activeService!.sendMessage(message);
    } else {
      log.warn('Send message not supported by current chat provider');
    }
  }

  sendFirstLoginMessage(userName?: string): void {
    if (!this.isReady()) return;
    if (this.activeService!.sendFirstLoginMessage) {
      this.activeService!.sendFirstLoginMessage(userName);
    } else {
      log.warn('Send first-login message not supported by current chat provider');
    }
  }

  reset(): void {
    if (!this.isReady()) return;
    if (this.activeService!.reset) {
      this.activeService!.reset();
    }
  }

  cleanup(): void {
    if (this.activeService) {
      this.activeService.cleanup();
      this.activeService = null;
    }
    this.isInitialized = false;
  }

  getCurrentProvider(): ChatServiceType {
    return getActiveProvider();
  }
}

export const supportChatService = new ChatService();
export type { ChatServiceType, ChatServiceConfig } from './ChatServiceInterface';
